import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import { HelpCircle, Plus, ArrowLeft, Send, Clock, CheckCircle, MessageCircle, Paperclip, Download, Image, X } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const SupportPage = () => {
  const { user } = useAuth();
  const { userTheme } = useTheme();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  // Get theme values
  const pageStyles = userTheme?.page || {};
  const supportBox = userTheme?.supportBox || {};
  const buttonStyles = userTheme?.buttons || {};
  
  // Fallback colors
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';
  const cardBg = supportBox.backgroundColor || '#FFFFFF';
  const cardBorder = supportBox.borderColor || '#E5E7EB';
  const textColor = supportBox.textColor || '#374151';
  const titleColor = supportBox.titleColor || '#111827';

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await api.get('/support/tickets');
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newSubject.trim() || !newMessage.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/support/tickets', {
        subject: newSubject,
        message: newMessage
      });
      toast.success('Ticket created successfully');
      setShowNewTicket(false);
      setNewSubject('');
      setNewMessage('');
      navigate(`/admin/support/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { bg: `${primaryColor}15`, color: primaryColor, icon: Clock },
      in_progress: { bg: '#FEF3C7', color: '#D97706', icon: MessageCircle },
      closed: { bg: '#F3F4F6', color: '#6B7280', icon: CheckCircle }
    };
    const style = styles[status] || styles.open;
    const Icon = style.icon;
    return (
      <span 
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
        style={{ backgroundColor: style.bg, color: style.color }}
      >
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  // If viewing single ticket
  if (id) {
    return <TicketDetail ticketId={id} userTheme={userTheme} />;
  }

  if (loading) {
    return (
      <Sidebar>
        <div 
          className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
        >
          <div 
            className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300"
            style={{ borderTopColor: primaryColor }}
          ></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div 
        className="p-8 min-h-screen"
        style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl" style={{ backgroundColor: primaryColor }}>
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 
                className="text-2xl font-semibold"
                style={{ color: pageStyles.titleColor || '#111827' }}
              >
                Support
              </h1>
              <p style={{ color: pageStyles.textColor || '#6B7280' }} className="text-sm">
                Get help from our support team
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowNewTicket(true)}
            className="text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:opacity-90 transition"
            style={{ backgroundColor: primaryColor }}
          >
            <Plus className="w-5 h-5" />
            New Ticket
          </button>
        </div>

        {/* New Ticket Modal */}
        {showNewTicket && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div 
              className="rounded-xl shadow-xl max-w-lg w-full p-6"
              style={{ backgroundColor: cardBg }}
            >
              <h2 className="text-xl font-bold mb-4" style={{ color: titleColor }}>
                Create New Ticket
              </h2>
              <form onSubmit={handleCreateTicket}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: titleColor }}>
                    Subject
                  </label>
                  <input
                    type="text"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg focus:ring-2"
                    style={{ border: `1px solid ${cardBorder}`, color: textColor }}
                    placeholder="Brief description of your issue"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2" style={{ color: titleColor }}>
                    Message
                  </label>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 rounded-lg focus:ring-2"
                    style={{ border: `1px solid ${cardBorder}`, color: textColor }}
                    placeholder="Describe your issue in detail..."
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowNewTicket(false)}
                    className="flex-1 px-4 py-2 rounded-lg hover:bg-gray-50"
                    style={{ border: `1px solid ${cardBorder}`, color: textColor }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {submitting ? 'Creating...' : 'Create Ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {tickets.length === 0 ? (
          <div 
            className="rounded-xl p-12 text-center"
            style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
          >
            <HelpCircle className="w-16 h-16 mx-auto mb-4" style={{ color: cardBorder }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: titleColor }}>
              No support tickets
            </h3>
            <p className="mb-4" style={{ color: textColor }}>
              Create a ticket to get help from our team.
            </p>
            <button
              onClick={() => setShowNewTicket(true)}
              className="text-white px-6 py-2 rounded-lg font-medium hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
            >
              Create Your First Ticket
            </button>
          </div>
        ) : (
          <div 
            className="rounded-xl shadow-sm overflow-hidden"
            style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
          >
            <div className="divide-y" style={{ '--tw-divide-color': cardBorder }}>
              {tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => navigate(`/admin/support/${ticket.id}`)}
                  className="w-full p-4 hover:bg-gray-50 transition-colors text-left flex items-center justify-between"
                >
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-mono text-sm" style={{ color: textColor }}>#{ticket.id}</span>
                      {getStatusBadge(ticket.status)}
                    </div>
                    <h3 className="font-medium" style={{ color: titleColor }}>{ticket.subject}</h3>
                    <p className="text-sm mt-1" style={{ color: textColor }}>
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <ArrowLeft className="w-5 h-5 rotate-180" style={{ color: cardBorder }} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

const isImageFile = (filename) => /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(filename);

const AttachmentDisplay = ({ attachment }) => {
  if (!attachment) return null;
  return (
    <div className="mt-2">
      {isImageFile(attachment.file_name) ? (
        <a href={attachment.file_url} target="_blank" rel="noopener noreferrer">
          <img
            src={attachment.file_url}
            alt={attachment.file_name}
            className="max-w-xs max-h-48 rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
          />
          <span className="flex items-center gap-1 text-xs text-gray-500 mt-1">
            <Image className="w-3 h-3" />
            {attachment.file_name}
          </span>
        </a>
      ) : (
        <a
          href={attachment.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 bg-white/60 rounded px-2 py-1 w-fit"
        >
          <Paperclip className="w-3 h-3" />
          {attachment.file_name}
          <Download className="w-3 h-3" />
        </a>
      )}
    </div>
  );
};

// Ticket Detail Component
const TicketDetail = ({ ticketId, userTheme }) => {
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const fileRef = React.useRef(null);
  const messagesContainerRef = React.useRef(null);

  // Get theme values
  const pageStyles = userTheme?.page || {};
  const supportBox = userTheme?.supportBox || {};
  const buttonStyles = userTheme?.buttons || {};

  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';
  const cardBg = supportBox.backgroundColor || '#FFFFFF';
  const cardBorder = supportBox.borderColor || '#E5E7EB';
  const textColor = supportBox.textColor || '#374151';
  const titleColor = supportBox.titleColor || '#111827';

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    if (!ticketId) return;
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/support/tickets/${ticketId}`);
        setTicket(response.data.ticket);
        setMessages(response.data.messages);
      } catch {
        // Silently ignore polling errors
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [ticketId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const fetchTicket = async () => {
    try {
      const response = await api.get(`/support/tickets/${ticketId}`);
      setTicket(response.data.ticket);
      setMessages(response.data.messages);
    } catch (error) {
      toast.error('Failed to load ticket');
      navigate('/admin/support');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      let res;
      if (attachment) {
        const formData = new FormData();
        formData.append('message', newMessage);
        formData.append('file', attachment);
        res = await api.post(`/support/tickets/${ticketId}/messages/with-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await api.post(`/support/tickets/${ticketId}/messages`, { message: newMessage });
      }
      // Update locally instead of refreshing
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      setAttachment(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div 
          className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
        >
          <div 
            className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300"
            style={{ borderTopColor: primaryColor }}
          ></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div 
        className="p-8 min-h-screen"
        style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
      >
        <button
          onClick={() => navigate('/admin/support')}
          className="flex items-center gap-2 mb-6 font-medium hover:opacity-80"
          style={{ color: primaryColor }}
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Tickets
        </button>

        {ticket && (
          <div 
            className="rounded-xl overflow-hidden mb-6"
            style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
          >
            <div className="px-6 py-4" style={{ backgroundColor: primaryColor }}>
              <div className="flex items-center gap-3 text-white/70 text-sm mb-1">
                <span>Ticket #{ticket.id}</span>
              </div>
              <h1 className="text-xl font-bold text-white">{ticket.subject}</h1>
            </div>
            
            <div className="p-6">
              {/* Messages */}
              <div ref={messagesContainerRef} className="space-y-4 mb-6 max-h-[500px] overflow-y-auto pr-2">
                {messages.map((msg) => {
                  const isAdmin = msg.is_admin || msg.sender_role === 'admin' || msg.sender_role === 'company_admin' || msg.sender_role === 'main_admin' || msg.sender_role === 'super_admin';
                  const isSuperAdmin = msg.sender_role === 'main_admin' || msg.sender_role === 'super_admin' || msg.sender_role === 'admin';
                  const isCompanyAdmin = msg.sender_role === 'company_admin';

                  let bgColor, senderLabel;
                  if (isSuperAdmin) {
                    bgColor = '#FFF7ED'; // light orange
                    senderLabel = 'Trados Support';
                  } else if (isCompanyAdmin) {
                    bgColor = '#ECFDF5'; // light green
                    senderLabel = 'Support';
                  } else {
                    bgColor = '#F9FAFB'; // gray
                    senderLabel = msg.sender_name || 'You';
                  }

                  return (
                    <div
                      key={msg.id}
                      className={`p-4 rounded-xl ${isAdmin ? 'ml-8' : 'mr-8'}`}
                      style={{
                        backgroundColor: bgColor,
                        border: `1px solid ${cardBorder}`
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium" style={{ color: titleColor }}>
                          {senderLabel}
                        </span>
                        <span className="text-xs" style={{ color: textColor }}>
                          {new Date(msg.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p style={{ color: textColor }}>{msg.message}</p>
                      <AttachmentDisplay attachment={msg.attachment} />
                    </div>
                  );
                })}
              </div>

              {/* Reply Form */}
              {ticket.status !== 'closed' && (
                <div className="space-y-2">
                  {attachment && (
                    <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                      <Paperclip className="w-3 h-3" />
                      <span className="truncate">{attachment.name}</span>
                      <button
                        onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ''; }}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <form onSubmit={handleSendMessage} className="flex gap-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 px-4 py-3 rounded-xl focus:ring-2"
                      style={{ border: `1px solid ${cardBorder}`, color: textColor }}
                    />
                    <input
                      type="file"
                      ref={fileRef}
                      className="hidden"
                      onChange={(e) => { if (e.target.files[0]) setAttachment(e.target.files[0]); }}
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="px-3 py-3 rounded-xl border hover:bg-gray-50"
                      style={{ borderColor: cardBorder, color: textColor }}
                      title="Attach file or photo"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Send className="w-5 h-5" />
                      Send
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default SupportPage;
